// ─────────────────────────────────────────────────────────────
// CloudflareOpen — Open a project from Cloudflare R2
//
// Shows the same "Browse Projects" dialog as the native Open,
// but populated with cloud projects from R2.
// Selecting a project downloads it to OPFS then loads it.
// ─────────────────────────────────────────────────────────────

import {Errors, RuntimeNotifier, Terminator} from "@opendaw/lib-std"
import {IconSymbol} from "@opendaw/studio-enums"
import {Surface} from "@/ui/surface/Surface"
import {Dialog} from "@/ui/components/Dialog"
import {createElement} from "@opendaw/lib-jsx"
import {StudioService} from "@/service/StudioService"
import {CloudflareHandler} from "./CloudflareHandler"
import {CloudflareBrowser} from "./CloudflareBrowser"

export namespace CloudflareOpen {

    export const open = async (service: StudioService): Promise<void> => {
        const handler = new CloudflareHandler()

        // Verify worker is reachable first
        try {
            await handler.alive()
        } catch {
            await RuntimeNotifier.info({
                headline: "Cloudflare Unavailable",
                message: "Could not reach the Cloudflare DAW worker. Check your connection and try again."
            })
            return
        }

        // Show the browse dialog — identical shell to the native Browse Projects dialog
        const {resolve, reject, promise} = Promise.withResolvers<void>()
        const lifecycle = new Terminator()

        const dialog: HTMLDialogElement = (
            <Dialog headline={"Open from Cloud"}
                    icon={IconSymbol.CloudFolder}
                    buttons={[{text: "Cancel", onClick: () => dialog.close()}]}
                    cancelable={true} style={{height: "30em"}}>
                <div style={{height: "2em"}}/>
                <CloudflareBrowser
                    handler={handler}
                    lifecycle={lifecycle}
                    select={async ([uuid, meta]) => {
                        dialog.close()
                        try {
                            await service.loadProject(uuid, meta)
                            resolve()
                        } catch (reason: unknown) {
                            if (!Errors.isAbort(reason)) {
                                reject(reason)
                            } else {
                                resolve()
                            }
                        }
                    }}/>
            </Dialog>
        ) as HTMLDialogElement

        dialog.oncancel = () => resolve()
        dialog.onkeydown = (event: KeyboardEvent) => {
            if (event.code === "Enter") {dialog.close()}
        }

        Surface.get().flyout.appendChild(dialog)
        dialog.showModal()

        return promise.finally(() => lifecycle.terminate())
    }
}
